export const BLOG_CATEGORIES = [
  "GEO", "AI Search", "ChatGPT", "Gemini", "Claude", "Perplexity",
  "Semantic SEO", "Entity SEO", "Structured Data", "Future Search",
];

export const AUTHORS = {
  "compubrain-ai": {
    slug: "compubrain-ai",
    name: "CompuBrain AI Research Team",
    role: "GEO Strategists & Engineers",
    bio: "The CompuBrain AI research team combines 20+ years of digital expertise with dedicated generative engine optimization practice, helping 5300+ brands become visible in AI search.",
  },
};

export const POSTS = [
  {
    slug: "what-is-generative-engine-optimization",
    title: "What is Generative Engine Optimization (GEO)? The Complete Guide",
    category: "GEO",
    excerpt: "A clear, practical definition of GEO, why it matters now, and how businesses win visibility inside AI answers.",
    author: "compubrain-ai",
    date: "2025-11-18",
    readingTime: 9,
    content: [
      { type: "p", text: "Generative Engine Optimization (GEO) is the practice of optimizing your brand so AI-powered engines — ChatGPT, Google AI Mode, Gemini, Claude and Perplexity — can discover, understand, trust and cite it inside generated answers. It is the natural successor to SEO for a world where answers are synthesised, not just ranked." },
      { type: "h2", text: "Why GEO matters now" },
      { type: "p", text: "AI assistants increasingly answer questions directly. Users get complete responses without clicking a single link. If AI does not name your brand in that answer, you are invisible at the exact moment a decision is made." },
      { type: "h2", text: "How GEO differs from SEO" },
      { type: "p", text: "Traditional SEO optimizes pages to rank as blue links using keywords and backlinks. GEO optimizes your brand as an entity — using facts, structured data, entity authority and citations — so AI systems recommend you within their answers." },
      { type: "h2", text: "The building blocks of GEO" },
      { type: "list", items: ["Entity authority and knowledge-graph presence", "Comprehensive structured data (JSON-LD schema)", "Answer-first, machine-readable content", "Authoritative citations and consistent brand data", "Technical foundations for AI crawlers"] },
      { type: "h2", text: "How to get started" },
      { type: "p", text: "Begin with an AI visibility audit to see how engines currently describe and cite you, then implement a prioritised roadmap across content, structured data, entities and authority." },
    ],
    faqs: [
      { q: "Is GEO replacing SEO?", a: "GEO builds on SEO rather than replacing it. Strong SEO foundations help, but GEO adds the entity, structured-data and citation work that AI engines reward." },
      { q: "How do I measure GEO?", a: "Track AI citations, brand mentions in answers, share of AI voice, and downstream qualified enquiries." },
    ],
  },
  {
    slug: "how-to-rank-in-chatgpt",
    title: "How to Get Your Business Cited by ChatGPT",
    category: "ChatGPT",
    excerpt: "Practical steps to make ChatGPT discover, trust and recommend your brand in its answers.",
    author: "compubrain-ai",
    date: "2025-11-25",
    readingTime: 7,
    content: [
      { type: "p", text: "ChatGPT now searches the web and cites sources. Getting cited requires being an entity ChatGPT can find, trust and quote. Here is how to earn that." },
      { type: "h2", text: "1. Become a clear entity" },
      { type: "p", text: "Ensure your brand is consistently described across the web, with structured profiles and, where relevant, a knowledge-graph presence." },
      { type: "h2", text: "2. Publish answer-first content" },
      { type: "p", text: "Lead with concise, factual answers to the questions your buyers ask. ChatGPT prefers content it can lift cleanly." },
      { type: "h2", text: "3. Implement structured data" },
      { type: "p", text: "Organization, FAQ and Service schema reduce ambiguity and increase citation likelihood." },
      { type: "h2", text: "4. Earn authoritative citations" },
      { type: "p", text: "Mentions from credible third-party sources strengthen the trust ChatGPT relies on when recommending brands." },
    ],
    faqs: [
      { q: "Can I pay to appear in ChatGPT?", a: "No. Organic citation is earned through entity authority, structured data and trustworthy content — the core of GEO." },
    ],
  },
  {
    slug: "seo-vs-geo",
    title: "SEO vs GEO: What Changes in the Age of AI Search",
    category: "AI Search",
    excerpt: "A side-by-side comparison of traditional SEO and Generative Engine Optimization.",
    author: "compubrain-ai",
    date: "2025-12-02",
    readingTime: 6,
    content: [
      { type: "p", text: "SEO and GEO share DNA but optimize for different outcomes. SEO wins rankings; GEO wins recommendations inside AI answers." },
      { type: "h2", text: "Different success metrics" },
      { type: "p", text: "SEO measures rankings and clicks. GEO measures citations, mentions and share of AI voice." },
      { type: "h2", text: "Different optimization targets" },
      { type: "p", text: "SEO targets keywords and backlinks. GEO targets entities, facts, structured data and trust signals." },
      { type: "h2", text: "Why you need both" },
      { type: "p", text: "Strong SEO still drives discovery; GEO ensures AI engines trust and recommend you. Together they future-proof your visibility." },
    ],
    faqs: [
      { q: "Should I stop doing SEO?", a: "No. Keep your SEO foundations and layer GEO on top to capture AI-driven discovery." },
    ],
  },
];

export const getPost = (slug) => POSTS.find((p) => p.slug === slug);
